import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Info } from 'lucide-react';
import { PaymentType } from '@/types';
import { FeeBreakdown } from './FeeBreakdown';
import { useEffect, useState, useRef } from 'react';
import { mercadoPagoService } from '@/api/mercadopago/mercadoPagoApi';
import type { MercadoPagoFeeInfo } from '@/types/mercadopago.model';

interface OrganizerIncomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  basePrice: number;
  commission: number;
}

export function OrganizerIncomeModal({
  isOpen,
  onClose,
  basePrice,
  commission,
}: OrganizerIncomeModalProps) {
  const [mercadoPagoFee, setMercadoPagoFee] =
    useState<MercadoPagoFeeInfo | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  // The basePrice is the tournament base price, commission is the GetMatch fee
  // Total tournament price = basePrice + commission
  const tournamentTotalPrice = basePrice + commission;

  // Let FeeBreakdown calculate the organizer income - we just need to provide the base data
  const organizerIncome = {
    [PaymentType.ALL]: 0, // Will be calculated by FeeBreakdown
    [PaymentType.CASH]: 0, // Will be calculated by FeeBreakdown
    [PaymentType.MERCADOPAGO]: 0, // Will be calculated by FeeBreakdown
  };

  const totalPrice = {
    [PaymentType.ALL]: tournamentTotalPrice,
    [PaymentType.CASH]: tournamentTotalPrice,
    [PaymentType.MERCADOPAGO]: tournamentTotalPrice,
  };

  useEffect(() => {
    const fetchMercadoPagoFee = async () => {
      try {
        const response = await mercadoPagoService.getFeeInfo();
        setMercadoPagoFee(response);
      } catch (error) {
        console.error('Error fetching Mercado Pago fee:', error);
      }
    };
    fetchMercadoPagoFee();
  }, []);

  if (!mercadoPagoFee) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="sm:max-w-[800px]"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (titleRef.current) {
              titleRef.current.focus();
            }
          }}
        >
          <DialogHeader>
            <div className="flex items-center space-x-2">
              <Info className="h-5 w-5 text-blue-600" />
              <DialogTitle ref={titleRef} tabIndex={-1}>
                Detalle de Ingreso del Organizador
              </DialogTitle>
            </div>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">
                Cargando información de comisiones...
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[800px]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (titleRef.current) {
            titleRef.current.focus();
          }
        }}
      >
        <DialogHeader>
          <div className="flex items-center space-x-2">
            <Info className="h-5 w-5 text-blue-600" />
            <DialogTitle ref={titleRef} tabIndex={-1}>
              Detalle de Ingreso del Organizador
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4 rounded-lg bg-blue-50 p-3">
            <p className="text-sm text-blue-800">
              Este desglose muestra cuánto recibirá el organizador del torneo
              después de descontar las comisiones correspondientes.
            </p>
          </div>

          <FeeBreakdown
            isDesktop={true}
            paymentType={PaymentType.ALL}
            organizerIncome={organizerIncome}
            totalPrice={totalPrice}
            mercadoPagoFee={mercadoPagoFee}
            fixedFeeAmount={commission}
            hasPriceError={false}
            isModal={true}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
